/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

export const IMAGE_UNAVAILABLE_SRC = '/assets/placeholder/image-unavailable.svg';
export const IMAGE_UNAVAILABLE_CLASS = 'image-unavailable';
export const IMAGE_UNAVAILABLE_KEY = 'translations.global.imageUnavailable';

const MARKER = 'imageUnavailable';

export function isImageUnavailable(img: HTMLImageElement): boolean {
  return img.dataset[MARKER] === 'true';
}

/**
 * Swaps a broken <img> for the shared placeholder. A file that is referenced but no longer in storage
 * used to render as the browser's broken-image glyph plus its alt text; this keeps the layout and tells
 * the reader what happened. Returns false when the element already carries the placeholder.
 */
export function applyImageFallback(img: HTMLImageElement, alt: string): boolean {
  if (isImageUnavailable(img)) {
    return false;
  }
  img.dataset[MARKER] = 'true';
  img.classList.add(IMAGE_UNAVAILABLE_CLASS);
  img.removeAttribute('srcset');
  img.alt = alt;
  img.src = IMAGE_UNAVAILABLE_SRC;
  return true;
}

/**
 * Capturing `error` listener for containers whose <img> come from innerHTML (the html preview, the
 * Tiptap document) — `error` does not bubble, so delegation has to happen in the capture phase.
 * Returns the detach function.
 */
export function attachImageFallbackListener(container: HTMLElement, alt: () => string): () => void {
  const handler = (event: Event) => {
    if (event.target instanceof HTMLImageElement) {
      applyImageFallback(event.target, alt());
    }
  };
  container.addEventListener('error', handler, true);
  return () => container.removeEventListener('error', handler, true);
}

/**
 * `<img ogImageFallback>`: on load error, optionally retries one alternative source (e.g. the original
 * upload behind a resized variant), then shows the placeholder and reports it.
 */
@Directive({
  selector: 'img[ogImageFallback]',
  standalone: true,
})
export class ImageFallbackDirective {
  /** Second source to try before giving up; empty means none. */
  @Input('ogImageFallback') retrySrc: string | null | undefined;
  @Output() imageUnavailable = new EventEmitter<void>();

  private readonly element = inject<ElementRef<HTMLImageElement>>(ElementRef);
  private readonly transloco = inject(TranslocoService);
  private retried = false;

  @HostListener('error')
  onError(): void {
    const img = this.element.nativeElement;
    if (isImageUnavailable(img)) {
      return;
    }
    if (!this.retried && this.retrySrc && this.retrySrc !== img.getAttribute('src')) {
      this.retried = true;
      img.src = this.retrySrc;
      return;
    }
    if (applyImageFallback(img, this.transloco.translate(IMAGE_UNAVAILABLE_KEY))) {
      this.imageUnavailable.emit();
    }
  }
}
